import React from "react";
import { Theme, alpha } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { BACKUP_DIR, DNS_PROVIDERS, DnsAuthority, Domain, domains, IP_ADINO, IP_VM63, leftovers, MAIN_SITE, RULES } from "./domains";
import { cleanDomain, CodeBlock, Expect, Hint, isDomain, oldPath, Step, targetUrl, useSteps } from "../guide/GuideParts";

// Interactive step-by-step guides for IT: find where a domain lives, change or create a redirect, change DNS.
// All commands are generated from the inputs, so nothing has to be typed by hand.

type GuideId = "find" | "change" | "new" | "plesk" | "dns" | "help";

const GUIDES: { id: GuideId; label: string; text: string }[] = [
  { id: "find", label: "1 · Where is my domain?", text: "Find out which server answers a domain and where its redirect has to be changed." },
  { id: "change", label: "2 · Change a redirect (VM63)", text: "Point an existing redirect on VM63 to another page, or add one old URL." },
  { id: "new", label: "3 · New domain redirect (VM63)", text: "Send a new domain to a page of loacker-recycling.com, with its own HTTPS certificate (like asr.ch)." },
  { id: "plesk", label: "4 · Redirect on Adino (Plesk)", text: "Create or change a redirect in the .htaccess file of a domain hosted at Adino." },
  { id: "dns", label: "5 · Change DNS", text: "Point a domain to another server: change its A record at the right DNS provider." },
  { id: "help", label: "Glossary & problems", text: "What nginx, .htaccess, DNS and the other words mean, and what to do when something fails." },
];

const useStyles = makeStyles()((theme: Theme) => ({
  root: { marginTop: theme.spacing(2), marginBottom: theme.spacing(6) },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 44 },
  },
  panel: { padding: theme.spacing(2.5), borderRadius: 12 },
  inputs: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: 10,
    background: alpha(theme.palette.primary.main, 0.05),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  },
  progress: { fontSize: 12.5, color: theme.palette.text.secondary, margin: theme.spacing(1, 0) },
  layers: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(1.5),
    margin: theme.spacing(2, 0),
  },
  layer: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    borderTop: "4px solid",
    background: theme.palette.background.paper,
    "& small": { display: "block", color: theme.palette.text.secondary, fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.5 },
    "& b": { display: "block", fontSize: 15, margin: "4px 0" },
    "& span": { fontSize: 12.5, color: theme.palette.text.secondary },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    "& th, & td": { textAlign: "left", padding: theme.spacing(0.75, 1), borderBottom: `1px solid ${theme.palette.divider}`, verticalAlign: "top" },
    "& th": { fontSize: 12, color: theme.palette.text.secondary, fontWeight: 600 },
    "& code": { fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 },
  },
  glossary: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(1.5),
    "& > div": { padding: theme.spacing(1.25, 1.5), borderRadius: 10, border: `1px solid ${theme.palette.divider}`, fontSize: 13, lineHeight: 1.5 },
    "& b": { display: "block", marginBottom: 2 },
  },
  mono: { fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: "0.92em" },
}));

const known = (domain: string) => domains.find((d) => d.domain === domain);
const serverNames = (domain: string, www: boolean) => (www ? `${domain} www.${domain}` : domain);
const Mono: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const { classes } = useStyles();
  return <code className={classes.mono}>{children}</code>;
};

const SSH_STEP = (
  <>
    <p>
      Open <b>Windows Terminal</b> (or PuTTY) and connect to the web server. Use your own SSH account of VM63. All commands with{" "}
      <Mono>sudo</Mono> ask for your password once.
    </p>
    <CodeBlock code={`ssh <your-user>@${IP_VM63}`} />
  </>
);

const Progress: React.FunctionComponent<{ count: number; total: number }> = ({ count, total }) => {
  const { classes } = useStyles();
  return (
    <div className={classes.progress}>
      {count} of {total} steps done{count === total ? " – finished 🎉" : ""}
    </div>
  );
};

const DomainInput: React.FunctionComponent<{
  value: string;
  onChange: (v: string) => void;
  label?: string;
  options?: Domain[];
  helper?: string;
}> = ({ value, onChange, label = "Domain", options = domains, helper }) => (
  <Autocomplete
    freeSolo
    size="small"
    options={options.map((d) => d.domain)}
    inputValue={value}
    onInputChange={(_, v) => onChange(v)}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        placeholder="example.ch"
        error={!!value && !isDomain(cleanDomain(value))}
        helperText={value && !isDomain(cleanDomain(value)) ? "Not a valid domain name" : helper}
      />
    )}
  />
);

const TargetInput: React.FunctionComponent<{ value: string; onChange: (v: string) => void; label?: string }> = ({
  value,
  onChange,
  label = "Target page on loacker-recycling.com",
}) => (
  <TextField
    size="small"
    label={label}
    placeholder="/ch/location/asr-recycling-sh/"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    helperText={
      value ? (
        <>
          Redirects to{" "}
          <a href={targetUrl(value)} target="_blank" rel="noreferrer">
            {targetUrl(value)}
          </a>{" "}
          – open it and check the page exists
        </>
      ) : (
        "A path like /at/location/weiss-sbg/ or a full https:// URL"
      )
    }
  />
);

// ---------- 1 · Where is my domain? ----------

const FindGuide: React.FunctionComponent<{ open: (guide: GuideId, domain: string) => void }> = ({ open }) => {
  const { classes } = useStyles();
  const [input, setInput] = React.useState("");
  const [ns, setNs] = React.useState<DnsAuthority | "">("");
  const [ip, setIp] = React.useState("");
  const [rule, setRule] = React.useState("");
  const domain = cleanDomain(input);
  const valid = isDomain(domain);
  const entry = valid ? known(domain) : undefined;
  React.useEffect(() => {
    setNs("");
    setIp("");
    setRule("");
  }, [domain]);

  // Known domains answer from the inventory; others through the questions below
  const dns: DnsAuthority | "" = entry ? entry.dns : ns;
  const a = entry?.a ?? ip;
  const scenario = entry && entry.scenario !== "unknown" ? entry.scenario : a === IP_ADINO ? "A" : a === IP_VM63 ? (rule === "301" ? "B" : rule === "wp" ? "C" : "") : "";

  return (
    <>
      <div className={classes.inputs}>
        <DomainInput value={input} onChange={setInput} helper="Pick a known domain or type any other" />
      </div>
      {!valid && <Hint>Every domain passes three layers: who controls its DNS, which IP its A record points to, and the rule on that server. Type a domain to see all three.</Hint>}
      {valid && (!entry || entry.scenario === "unknown") && (
        <>
          <Hint type="warn">
            {entry ? `${domain} has a file on VM63 but its DNS is not documented yet.` : `${domain} is not in the inventory.`} Find out with these two commands on your PC:
          </Hint>
          <CodeBlock label="Windows PC · PowerShell / cmd" code={`nslookup -type=NS ${domain}\nnslookup ${domain}`} />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2 }}>
            <div>
              <Typography variant="subtitle2">What does the first command show as “nameserver”?</Typography>
              <RadioGroup value={ns} onChange={(e) => setNs(e.target.value as DnsAuthority)}>
                <FormControlLabel value="adino" control={<Radio size="small" />} label="ns1/ns2.profiwebspace.at → Adino" />
                <FormControlLabel value="azure" control={<Radio size="small" />} label="ns…azure-dns… → Azure DNS" />
                <FormControlLabel value="unknown" control={<Radio size="small" />} label="Something else" />
              </RadioGroup>
            </div>
            <div>
              <Typography variant="subtitle2">Which “Address” does the second command show?</Typography>
              <RadioGroup value={ip} onChange={(e) => setIp(e.target.value)}>
                <FormControlLabel value={IP_ADINO} control={<Radio size="small" />} label={`${IP_ADINO} → Adino Plesk`} />
                <FormControlLabel value={IP_VM63} control={<Radio size="small" />} label={`${IP_VM63} → Azure VM63 (nginx)`} />
                <FormControlLabel value="other" control={<Radio size="small" />} label="Another IP or none" />
              </RadioGroup>
            </div>
            {ip === IP_VM63 && (
              <div>
                <Typography variant="subtitle2">On VM63, what does this command show?</Typography>
                <CodeBlock code={`grep -n "return 301\\|try_files" /etc/nginx/sites-available/${domain}`} />
                <RadioGroup value={rule} onChange={(e) => setRule(e.target.value)}>
                  <FormControlLabel value="301" control={<Radio size="small" />} label="Lines with return 301" />
                  <FormControlLabel value="wp" control={<Radio size="small" />} label="Only try_files … index.php" />
                  <FormControlLabel value="none" control={<Radio size="small" />} label="No such file" />
                </RadioGroup>
              </div>
            )}
          </Box>
        </>
      )}
      {valid && (dns || a) && (
        <>
          <div className={classes.layers}>
            <div className={classes.layer} style={{ borderTopColor: "#1565c0" }}>
              <small>1 · DNS authority</small>
              <b>{dns === "adino" || dns === "azure" ? DNS_PROVIDERS[dns].name : dns === "unknown" ? "Other provider" : "–"}</b>
              <span>{dns === "adino" || dns === "azure" ? `Change records in: ${DNS_PROVIDERS[dns].panel}` : "Ask who registered the domain"}</span>
            </div>
            <div className={classes.layer} style={{ borderTopColor: "#005f98" }}>
              <small>2 · A record</small>
              <b className={classes.mono}>{a === "other" ? "other IP" : a || "–"}</b>
              <span>{a === IP_ADINO ? "Adino web hosting (Plesk)" : a === IP_VM63 ? "Azure VM63 with nginx" : entry?.aNote ?? "Not one of our web servers"}</span>
            </div>
            <div className={classes.layer} style={{ borderTopColor: scenario === "A" ? "#ef6c00" : "#2e7d32" }}>
              <small>3 · Rule on that server</small>
              <b>
                {scenario === "A"
                  ? ".htaccess in Plesk"
                  : scenario === "B"
                  ? `nginx file ${entry?.nginxFile ?? domain}`
                  : scenario === "C" || scenario === "main"
                  ? "WordPress (no redirect)"
                  : rule === "none"
                  ? "No file: catch-all answers"
                  : "–"}
              </b>
              <span>{entry?.target ? `→ ${MAIN_SITE}${entry.target}` : entry?.note ?? ""}</span>
            </div>
          </div>
          {scenario === "A" && <Button variant="contained" onClick={() => open("plesk", domain)}>Change the redirect in Plesk</Button>}
          {scenario === "B" && <Button variant="contained" onClick={() => open("change", domain)}>Change the redirect on VM63</Button>}
          {(scenario === "C" || scenario === "main") && (
            <Hint type="warn">
              This domain shows WordPress content and has no redirect. Content and domain mapping are managed by the agency in WordPress. To make it a
              redirect instead, the agency has to change its Forge site – ask them first.
            </Hint>
          )}
          {(rule === "none" || a === "other") && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="contained" onClick={() => open("new", domain)}>Create a new redirect on VM63</Button>
              <Button variant="outlined" onClick={() => open("plesk", domain)}>…or on Adino Plesk</Button>
              <Button variant="outlined" onClick={() => open("dns", domain)}>Change its DNS first</Button>
            </Box>
          )}
          {entry?.forge && scenario === "B" && (
            <Hint type="warn">This file was created by Forge. If the agency redeploys the site in Forge, manual changes are lost – keep the backup and tell the agency.</Hint>
          )}
        </>
      )}
    </>
  );
};

// ---------- 2 · Change a redirect on VM63 ----------

const ChangeGuide: React.FunctionComponent<{ initial: string }> = ({ initial }) => {
  const [input, setInput] = React.useState(initial);
  const [mode, setMode] = React.useState<"whole" | "path">("whole");
  const [target, setTarget] = React.useState("");
  const [from, setFrom] = React.useState("");
  const site = cleanDomain(input);
  const entry = known(site);
  const url = targetUrl(target);
  const old = oldPath(from);
  const ready = isDomain(site) && !!url && (mode === "whole" || !!old);
  const { isDone, toggle, count } = useSteps(`${site}|${mode}|${url}|${old}`);
  React.useEffect(() => {
    if (entry?.target && !target) setTarget(entry.target);
  }, [site]);

  const snippet =
    mode === "whole"
      ? `    location / {\n        return 301 ${url};\n    }`
      : [old, ...(old.endsWith("/") && old.length > 1 ? [old.slice(0, -1)] : [])]
          .map((p) => `    location = ${p} { return 301 ${url}; }`)
          .join("\n");

  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2, p: 2, mb: 1, borderRadius: 2.5, bgcolor: "action.hover" }}>
        <DomainInput value={input} onChange={setInput} label="Site (file on VM63)" options={domains.filter((d) => d.scenario === "B")} />
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="whole">Whole domain → one page</ToggleButton>
          <ToggleButton value="path">One old URL → one page</ToggleButton>
        </ToggleButtonGroup>
        {mode === "path" && <TextField size="small" label="Old URL path on the domain" placeholder="/de/kontakt/" value={from} onChange={(e) => setFrom(e.target.value)} />}
        <TargetInput value={target} onChange={setTarget} label="New target page" />
      </Box>
      {isDomain(site) && entry && entry.scenario !== "B" && (
        <Hint type="danger">
          {site} is not a VM63 redirect (scenario {entry.scenario}). {entry.scenario === "A" ? "Use guide 4 (Plesk)." : "It shows WordPress content – ask the agency."}
        </Hint>
      )}
      {isDomain(site) && !entry && <Hint type="warn">{site} is not in the inventory. Check with guide 1 that its DNS points to VM63 and that the file exists.</Hint>}
      {!ready && <Hint>Fill in the fields above – the commands below are generated from them.</Hint>}
      {ready && (
        <>
          {entry?.forge && <Hint type="warn">Forge file: do not touch the lines with <Mono># FORGE CONFIG</Mono> / <Mono># FORGE SSL</Mono>, and tell the agency about the change afterwards.</Hint>}
          <Progress count={count} total={9} />
          <Step n={1} title="Connect to VM63" where="pc" done={isDone(1)} onToggle={toggle(1)}>
            {SSH_STEP}
          </Step>
          <Step n={2} title="Look at the current redirect" where="vm63" done={isDone(2)} onToggle={toggle(2)}>
            <p>Shows the line numbers of the domain names and of the current redirects. Nothing is changed.</p>
            <CodeBlock code={`grep -n "server_name\\|listen\\|location\\|return 301" /etc/nginx/sites-available/${site}`} />
            <Expect>
              usually two <Mono>server_name</Mono> lines (one block for port 80, one for 443) and the <Mono>return 301</Mono> lines.
            </Expect>
          </Step>
          <Step n={3} title="Copy the file to your workspace and make a backup" where="vm63" done={isDone(3)} onToggle={toggle(3)}>
            <p>You never edit the live file directly. The <Mono>.bak</Mono> copy is your way back.</p>
            <CodeBlock code={`mkdir -p ${BACKUP_DIR}\ncp /etc/nginx/sites-available/${site} ${BACKUP_DIR}/${site}\ncp /etc/nginx/sites-available/${site} ${BACKUP_DIR}/${site}.bak`} />
          </Step>
          <Step n={4} title="Edit the copy" where="winscp" done={isDone(4)} onToggle={toggle(4)}>
            <p>
              In WinSCP, connect to <Mono>{IP_VM63}</Mono>, open <Mono>{`${BACKUP_DIR}/${site}`}</Mono> with the editor (F4) and{" "}
              {mode === "whole" ? (
                <>
                  replace <b>every</b> <Mono>location / {"{ … }"}</Mono> block (one per <Mono>server</Mono> block) with:
                </>
              ) : (
                <>
                  add these lines inside the <Mono>server</Mono> block with <Mono>listen 443</Mono>, just <b>before</b> <Mono>location / {"{"}</Mono>. If the port 80
                  block also has redirects, add them there too:
                </>
              )}
            </p>
            <CodeBlock code={snippet} />
            {mode === "path" && <Hint>Both variants, with and without the last slash, are added because old links use both. <Mono>location =</Mono> matches exactly this path only.</Hint>}
            <Hint>Every line inside a block ends with <Mono>;</Mono> and every <Mono>{"{"}</Mono> needs its <Mono>{"}"}</Mono>. Save with Ctrl+S.</Hint>
          </Step>
          <Step n={5} title="Put the edited file live" where="vm63" done={isDone(5)} onToggle={toggle(5)}>
            <CodeBlock code={`sudo cp ${BACKUP_DIR}/${site} /etc/nginx/sites-available/${site}`} />
          </Step>
          <Step n={6} title="Test the configuration – never skip this" where="vm63" done={isDone(6)} onToggle={toggle(6)}>
            <p>nginx checks all files without applying them. A broken file would take down <b>all</b> websites on VM63 at the next reload.</p>
            <CodeBlock code="sudo nginx -t" />
            <Expect>
              <Mono>syntax is ok</Mono> and <Mono>test is successful</Mono>. Anything else: do not continue – fix the file (the message names the line) or roll back (below).
            </Expect>
          </Step>
          <Step n={7} title="Apply the change" where="vm63" done={isDone(7)} onToggle={toggle(7)}>
            <p>A reload applies the new configuration without interrupting the websites.</p>
            <CodeBlock code="sudo systemctl reload nginx" />
          </Step>
          <Step n={8} title="Test from your PC" where="pc" done={isDone(8)} onToggle={toggle(8)}>
            <CodeBlock code={mode === "whole" ? `curl.exe -I https://${site}\ncurl.exe -I https://www.${site}` : `curl.exe -I https://${site}${old}`} />
            <Expect>
              <Mono>HTTP/1.1 301 Moved Permanently</Mono> and <Mono>Location: {url}</Mono>. Then open the domain in a private browser window (browsers cache 301s).
            </Expect>
          </Step>
          <Step n={9} title="Document and inform" where="browser" done={isDone(9)} onToggle={toggle(9)}>
            <p>Update the inventory on this page (domains.ts) and {entry?.forge ? "inform the agency in writing, because Forge may overwrite the file." : "keep the final file in the backup folder."}</p>
          </Step>
          <Hint type="danger">
            <b>Something wrong? Roll back</b> to the backup from step 3:
            <CodeBlock code={`sudo cp ${BACKUP_DIR}/${site}.bak /etc/nginx/sites-available/${site}\nsudo nginx -t && sudo systemctl reload nginx`} />
          </Hint>
        </>
      )}
    </>
  );
};

// ---------- 3 · New domain redirect on VM63 ----------

const NewGuide: React.FunctionComponent<{ initial: string; open: (guide: GuideId, domain: string) => void }> = ({ initial, open }) => {
  const [input, setInput] = React.useState(initial);
  const [www, setWww] = React.useState(true);
  const [target, setTarget] = React.useState("");
  const domain = cleanDomain(input);
  const url = targetUrl(target);
  const entry = known(domain);
  const ready = isDomain(domain) && !!url;
  const names = serverNames(domain, www);
  const hosts = www ? [domain, `www.${domain}`] : [domain];
  const { isDone, toggle, count } = useSteps(`${domain}|${www}|${url}`);

  const http = `sudo tee /etc/nginx/sites-available/${domain} > /dev/null <<'EOF'
server {
    listen 80;
    listen [::]:80;
    server_name ${names};
    server_tokens off;

    location ^~ /.well-known/acme-challenge/ {
        root /var/www/html;
    }
    location / {
        return 301 ${url};
    }
}
EOF
cat /etc/nginx/sites-available/${domain}`;

  const https = `sudo tee -a /etc/nginx/sites-available/${domain} > /dev/null <<'EOF'

server {
    listen 443 ssl http2;
    listen [::]:443 ssl http2;
    server_name ${names};
    server_tokens off;

    ssl_certificate     /etc/letsencrypt/live/${domain}/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/${domain}/privkey.pem;
    ssl_protocols TLSv1.2 TLSv1.3;

    access_log off;
    error_log /var/log/nginx/${domain}-error.log error;

    location / {
        return 301 ${url};
    }
}
EOF
cat /etc/nginx/sites-available/${domain}`;

  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2, p: 2, mb: 1, borderRadius: 2.5, bgcolor: "action.hover" }}>
        <DomainInput value={input} onChange={setInput} label="New domain" options={[]} helper="Without www, e.g. example.ch" />
        <TargetInput value={target} onChange={setTarget} />
        <FormControlLabel control={<Checkbox checked={www} onChange={(e) => setWww(e.target.checked)} />} label={`Also redirect www.${isDomain(domain) ? domain : "<domain>"}`} />
      </Box>
      {entry && (
        <Hint type="danger">
          {domain} already exists ({entry.nginxFile ? "file on VM63" : "Adino Plesk"}). Use guide {entry.scenario === "A" ? "4" : "2"} instead – creating it again would break it.
        </Hint>
      )}
      {!ready && <Hint>Fill in the domain and the target page – the commands below are generated from them.</Hint>}
      {ready && !entry && (
        <>
          <Hint>
            <b>Why two phases?</b> HTTPS needs a certificate for the domain itself. Let's Encrypt only issues it when the domain already answers on HTTP (port 80). So first HTTP and
            the certificate, then HTTPS.
          </Hint>
          <Progress count={count} total={12} />
          <Step n={1} title="Point the DNS to VM63" where="pc" done={isDone(1)} onToggle={toggle(1)}>
            <p>
              The A record of <Mono>@</Mono>{www && <> and <Mono>www</Mono></>} must point to <Mono>{IP_VM63}</Mono>. If it does not yet, do it first with{" "}
              <Button size="small" onClick={() => open("dns", domain)}>guide 5</Button>. Then check:
            </p>
            <CodeBlock code={hosts.map((h) => `nslookup ${h}`).join("\n")} />
            <Expect>
              <Mono>Address: {IP_VM63}</Mono> for {www ? "both names" : "the domain"}.
            </Expect>
          </Step>
          <Step n={2} title="Check the target page exists" where="browser" done={isDone(2)} onToggle={toggle(2)}>
            <p>
              Open <a href={url} target="_blank" rel="noreferrer">{url}</a>. It must show the right page, not a 404.
            </p>
          </Step>
          <Step n={3} title="Connect to VM63" where="pc" done={isDone(3)} onToggle={toggle(3)}>
            {SSH_STEP}
          </Step>
          <Step n={4} title="Create the configuration file (HTTP only)" where="vm63" done={isDone(4)} onToggle={toggle(4)}>
            <p>Paste the whole block at once. It writes the file and prints it.</p>
            <CodeBlock code={http} />
            <Expect>
              the file content, with the line <Mono>server_name {names};</Mono> exactly like this – no brackets or links (some chat tools turn www… into a link when copying).
            </Expect>
            <Hint>
              The <Mono>acme-challenge</Mono> part is not a website: Let's Encrypt uses this empty folder for a few seconds to check that you own the domain, at every renewal. Do not
              remove it.
            </Hint>
          </Step>
          <Step n={5} title="Activate the site" where="vm63" done={isDone(5)} onToggle={toggle(5)}>
            <p>
              nginx only reads the files that have a link in <Mono>sites-enabled</Mono>. The link activates the site.
            </p>
            <CodeBlock code={`sudo mkdir -p /var/www/html\nsudo ln -sf /etc/nginx/sites-available/${domain} /etc/nginx/sites-enabled/${domain}\nsudo nginx -t\nsudo systemctl reload nginx`} />
            <Expect>
              <Mono>syntax is ok</Mono> and <Mono>test is successful</Mono> from <Mono>nginx -t</Mono>. If not: stop and fix the file before the reload.
            </Expect>
          </Step>
          <Step n={6} title="Test HTTP" where="pc" done={isDone(6)} onToggle={toggle(6)}>
            <CodeBlock code={hosts.map((h) => `curl.exe -I http://${h}`).join("\n")} />
            <Expect>
              <Mono>301 Moved Permanently</Mono> and <Mono>Location: {url}</Mono>. https:// still fails at this point – that is expected.
            </Expect>
          </Step>
          <Step n={7} title="Get the SSL certificate" where="vm63" done={isDone(7)} onToggle={toggle(7)}>
            <CodeBlock
              code={`which certbot || sudo apt install -y certbot\nsudo certbot certonly --webroot -w /var/www/html ${hosts.map((h) => `-d ${h}`).join(" ")} --deploy-hook "systemctl reload nginx"`}
            />
            <p>
              Only the first time certbot asks: an <b>email</b> (use a team mailbox), accept the terms (<b>A</b>), share the email with EFF (<b>N</b>).
            </p>
            <Expect>
              <Mono>Successfully received certificate</Mono> and the files in <Mono>/etc/letsencrypt/live/{domain}/</Mono>.
            </Expect>
          </Step>
          <Step n={8} title="Add HTTPS to the same file" where="vm63" done={isDone(8)} onToggle={toggle(8)}>
            <p>
              <Mono>tee -a</Mono> <b>appends</b> the block; the HTTP block stays.
            </p>
            <CodeBlock code={https} />
            <Expect>
              two <Mono>server</Mono> blocks: one with <Mono>listen 80</Mono>, one with <Mono>listen 443 ssl</Mono>.
            </Expect>
          </Step>
          <Step n={9} title="Test and apply" where="vm63" done={isDone(9)} onToggle={toggle(9)}>
            <CodeBlock code={`sudo nginx -t\nsudo systemctl reload nginx`} />
            <Expect>
              <Mono>test is successful</Mono>. A message about a missing certificate file means step 7 did not succeed.
            </Expect>
          </Step>
          <Step n={10} title="Test HTTPS" where="pc" done={isDone(10)} onToggle={toggle(10)}>
            <CodeBlock code={hosts.map((h) => `curl.exe -I https://${h}`).join("\n")} />
            <Expect>
              <Mono>301</Mono> with <Mono>Location: {url}</Mono>. Then open it in a private browser window: no certificate warning.
            </Expect>
          </Step>
          <Step n={11} title="Check the automatic renewal" where="vm63" done={isDone(11)} onToggle={toggle(11)}>
            <CodeBlock code="sudo certbot renew --dry-run" />
            <Expect>
              <Mono>Congratulations, all simulated renewals succeeded</Mono>. certbot renews by itself before expiry and reloads nginx.
            </Expect>
          </Step>
          <Step n={12} title="Keep a copy, document and inform" where="vm63" done={isDone(12)} onToggle={toggle(12)}>
            <CodeBlock code={`cp /etc/nginx/sites-available/${domain} ${BACKUP_DIR}/${domain}\nsudo ls /etc/letsencrypt/live/${domain}/`} />
            <p>
              Add the domain to the inventory (domains.ts: DNS, A record, file, certificate, target) and inform the agency in writing that <Mono>{domain}</Mono> was added manually on
              VM63, outside Forge.
            </p>
          </Step>
          <Hint type="danger">
            <b>Undo everything</b> (removes the site; the certificate stays and does no harm):
            <CodeBlock code={`sudo rm /etc/nginx/sites-enabled/${domain}\nsudo nginx -t && sudo systemctl reload nginx`} />
          </Hint>
        </>
      )}
    </>
  );
};

// ---------- 4 · Redirect on Adino Plesk ----------

const PleskGuide: React.FunctionComponent<{ initial: string; open: (guide: GuideId, domain: string) => void }> = ({ initial, open }) => {
  const [input, setInput] = React.useState(initial);
  const [target, setTarget] = React.useState("");
  const domain = cleanDomain(input);
  const entry = known(domain);
  const url = targetUrl(target);
  const ready = isDomain(domain) && !!url;
  const { isDone, toggle, count } = useSteps(`${domain}|${url}`);
  React.useEffect(() => {
    if (entry?.target && !target) setTarget(entry.target);
  }, [domain]);

  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2, p: 2, mb: 1, borderRadius: 2.5, bgcolor: "action.hover" }}>
        <DomainInput value={input} onChange={setInput} options={domains.filter((d) => d.scenario === "A")} />
        <TargetInput value={target} onChange={setTarget} />
      </Box>
      {entry && entry.scenario !== "A" && (
        <Hint type="danger">
          {domain} points to VM63, not to Adino. A .htaccess rule in Plesk would have <b>no effect</b>. Use guide 2 instead.
        </Hint>
      )}
      {!ready && <Hint>Fill in the domain and the target page – the rule below is generated from them.</Hint>}
      {ready && (!entry || entry.scenario === "A") && (
        <>
          <Progress count={count} total={6} />
          <Step n={1} title="Check the domain points to Adino" where="pc" done={isDone(1)} onToggle={toggle(1)}>
            <CodeBlock code={`nslookup ${domain}\nnslookup www.${domain}`} />
            <Expect>
              <Mono>Address: {IP_ADINO}</Mono>. Another address: change the DNS first (<Button size="small" onClick={() => open("dns", domain)}>guide 5</Button>).
            </Expect>
          </Step>
          <Step n={2} title="Open the domain in Plesk" where="plesk" done={isDone(2)} onToggle={toggle(2)}>
            <p>
              Sign in to Plesk (<Mono>srv280.adino.at</Mono>) → <b>Websites &amp; Domains</b> → <Mono>{domain}</Mono>. If the domain is not listed, it first has to be added in Plesk.
            </p>
          </Step>
          <Step n={3} title="Back up the .htaccess file" where="plesk" done={isDone(3)} onToggle={toggle(3)}>
            <p>
              <b>File Manager</b> → select <Mono>.htaccess</Mono> → <b>Copy</b> → name it <Mono>.htaccess.bak</Mono>. If there is no .htaccess yet, create it with <b>+ → Create File</b>.
            </p>
          </Step>
          <Step n={4} title="Add the redirect rule" where="plesk" done={isDone(4)} onToggle={toggle(4)}>
            <p>
              Open <Mono>.htaccess</Mono> in the editor and add at the end (if the file already has a <Mono>RewriteRule</Mono> to loacker-recycling.com, replace it):
            </p>
            <CodeBlock label=".htaccess (Apache)" code={`RewriteEngine on\nRewriteRule ^ ${url}? [R=301,L]`} />
            <Hint>
              <Mono>^</Mono> matches every address of the domain, the <Mono>?</Mono> at the end drops old parameters, <Mono>R=301</Mono> means “moved permanently”, <Mono>L</Mono> stops
              further rules. kuster-recycling.ch uses two equivalent rules (<Mono>^$</Mono> and <Mono>^.+$</Mono>).
            </Hint>
          </Step>
          <Step n={5} title="HTTPS certificate" where="plesk" done={isDone(5)} onToggle={toggle(5)}>
            <p>
              Domain → <b>SSL/TLS Certificates</b> → <b>Install</b> a free Let's Encrypt certificate, tick the domain <b>and</b> <Mono>www.{domain}</Mono>. Skip if a valid one is already
              installed.
            </p>
          </Step>
          <Step n={6} title="Test from your PC" where="pc" done={isDone(6)} onToggle={toggle(6)}>
            <CodeBlock code={`curl.exe -I https://${domain}\ncurl.exe -I https://www.${domain}`} />
            <Expect>
              <Mono>301</Mono> and <Mono>Location: {url}</Mono>. Wrong? Copy <Mono>.htaccess.bak</Mono> back to <Mono>.htaccess</Mono>.
            </Expect>
          </Step>
        </>
      )}
    </>
  );
};

// ---------- 5 · Change DNS ----------

const DnsGuide: React.FunctionComponent<{ initial: string }> = ({ initial }) => {
  const [input, setInput] = React.useState(initial);
  const [ns, setNs] = React.useState<DnsAuthority | "">("");
  const [server, setServer] = React.useState(IP_VM63);
  const domain = cleanDomain(input);
  const entry = known(domain);
  const provider: DnsAuthority | "" = entry && entry.dns !== "unknown" ? entry.dns : ns;
  const { isDone, toggle, count } = useSteps(`${domain}|${provider}|${server}`);

  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2, p: 2, mb: 1, borderRadius: 2.5, bgcolor: "action.hover" }}>
        <DomainInput value={input} onChange={setInput} />
        <div>
          <Typography variant="subtitle2">Where should the domain go?</Typography>
          <RadioGroup row value={server} onChange={(e) => setServer(e.target.value)}>
            <FormControlLabel value={IP_VM63} control={<Radio size="small" />} label={`VM63 nginx (${IP_VM63})`} />
            <FormControlLabel value={IP_ADINO} control={<Radio size="small" />} label={`Adino Plesk (${IP_ADINO})`} />
          </RadioGroup>
        </div>
      </Box>
      {!isDomain(domain) && <Hint>Type the domain whose DNS you want to change.</Hint>}
      {isDomain(domain) && (
        <>
          <Hint type="warn">Change only A and CNAME records for the website. Never touch MX, TXT/SPF, autodiscover, mail or SIP – e-mail and phones depend on them.</Hint>
          <Progress count={count} total={4} />
          <Step n={1} title="Find the DNS authority" where="pc" done={isDone(1)} onToggle={toggle(1)}>
            <CodeBlock code={`nslookup -type=NS ${domain}`} />
            {entry && entry.dns !== "unknown" ? (
              <Expect>
                {DNS_PROVIDERS[entry.dns].ns.join(", ")} – {DNS_PROVIDERS[entry.dns].name} (from the inventory).
              </Expect>
            ) : (
              <RadioGroup row value={ns} onChange={(e) => setNs(e.target.value as DnsAuthority)}>
                <FormControlLabel value="adino" control={<Radio size="small" />} label="profiwebspace.at → Adino" />
                <FormControlLabel value="azure" control={<Radio size="small" />} label="azure-dns → Azure" />
                <FormControlLabel value="unknown" control={<Radio size="small" />} label="Something else" />
              </RadioGroup>
            )}
            <Hint>Only the DNS authority counts. Records of the same domain in another panel have no effect (asr.ch was changed in nsentry once and nothing happened – it is in Azure).</Hint>
          </Step>
          {provider === "unknown" && <Hint type="danger">This domain is registered with another provider. Find out who manages it before changing anything.</Hint>}
          {(provider === "adino" || provider === "azure") && (
            <>
              <Step n={2} title={`Change the A record in ${DNS_PROVIDERS[provider].panel}`} where={provider === "adino" ? "adino" : "azure"} done={isDone(2)} onToggle={toggle(2)}>
                <ul>
                  {DNS_PROVIDERS[provider].how.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
                <p>
                  New value for <Mono>@</Mono> and <Mono>www</Mono>: <Mono>{server}</Mono>
                </p>
              </Step>
              <Step n={3} title="Check at the DNS authority itself" where="pc" done={isDone(3)} onToggle={toggle(3)}>
                <p>Asks the provider directly, so local caches do not matter.</p>
                <CodeBlock code={`nslookup ${domain} ${DNS_PROVIDERS[provider].ns[0]}\nnslookup www.${domain} ${DNS_PROVIDERS[provider].ns[0]}`} />
                <Expect>
                  <Mono>Address: {server}</Mono>
                </Expect>
              </Step>
              <Step n={4} title="Check from your PC (after about 5 minutes)" where="pc" done={isDone(4)} onToggle={toggle(4)}>
                <CodeBlock code={`ipconfig /flushdns\nnslookup ${domain}`} />
                <p>
                  Still the old IP? The internal DNS server <Mono>atgolos010</Mono> may cache it – an admin can clear it with <Mono>Clear-DnsServerCache</Mono> on that server.
                </p>
              </Step>
            </>
          )}
        </>
      )}
    </>
  );
};

// ---------- Glossary & problems ----------

const GLOSSARY: [string, string][] = [
  ["DNS", "The internet's phone book: translates a name like asr.ch into the IP address of a server."],
  ["Name server (NS)", "The DNS provider that is in charge of a domain (Adino or Azure). Only changes there have an effect."],
  ["A record", "The entry that says which IP address a name points to. @ means the domain itself, www the www. name."],
  ["CNAME", "An alias: www.asr.ch → asr.ch, so www gets the same IP."],
  ["Web server", "The program that answers the browser. On VM63 it is nginx, at Adino it is Apache (managed through Plesk)."],
  ["nginx", "The web server on VM63. Every domain has its own small text file that says what to do with its visitors."],
  ["sites-available / sites-enabled", "sites-available holds the files; a link in sites-enabled switches a file on. Without the link nginx ignores it."],
  ["server_name", "The line in an nginx file with the domain names this file is responsible for."],
  ["return 301", "nginx rule: “this address moved permanently to …”. Browsers and Google follow it."],
  [".htaccess", "A small rules file in the folder of a website at Adino (Apache). RewriteRule … [R=301] is the same as nginx's return 301."],
  ["Plesk", "Adino's web panel to manage hosted domains, their files and certificates."],
  ["Laravel Forge", "The agency's tool that creates and updates sites on VM63. It may overwrite manual changes."],
  ["SSL certificate / Let's Encrypt", "Needed for https://. Each domain needs its own; Let's Encrypt issues them for free, certbot renews them automatically."],
  ["sudo", "Runs a command as administrator on Linux. Asks for your password."],
  ["SSH / WinSCP", "SSH opens a command line on the server; WinSCP shows its files like Explorer and has an editor."],
  ["curl.exe -I", "Asks a website only for its headers. Shows the 301 and the target (Location) without browser caching."],
];

const PROBLEMS: [string, string, string][] = [
  ["Blank page / empty response", "No file or no link in sites-enabled, so the catch-all answers", "ls -l /etc/nginx/sites-enabled/"],
  ["www does not redirect", "server_name wrong (brackets from copy-paste) or no DNS for www", "grep server_name /etc/nginx/sites-available/<domain> · nslookup www.<domain>"],
  ["HTTPS: SEC_E_ILLEGAL_MESSAGE / TLS error", "No certificate or no port 443 block yet", "Guide 3, steps 7–9"],
  ["certbot fails the http-01 challenge", "DNS does not point to VM63 yet, port 80 blocked, or the acme-challenge part is missing", "nslookup <domain> · guide 3 step 4"],
  ["sudo: certbot: command not found", "certbot is not installed", "sudo apt update && sudo apt install -y certbot"],
  ["nginx -t fails", "Typo (missing ; or }) or a certificate path that does not exist", "Fix the line named in the message, or roll back. Never reload before nginx -t passes"],
  ["Old page still shows after the change", "Browser cached the old 301, or DNS cache", "Private window · curl.exe -I · ipconfig /flushdns"],
  ["Change in DNS panel has no effect", "Edited at the wrong provider", "nslookup -type=NS <domain> · guide 5"],
];

const HelpGuide: React.FunctionComponent = () => {
  const { classes } = useStyles();
  return (
    <>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Rules
      </Typography>
      {RULES.map((r) => (
        <Hint key={r} type="warn">
          {r}
        </Hint>
      ))}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Glossary
      </Typography>
      <div className={classes.glossary}>
        {GLOSSARY.map(([term, text]) => (
          <div key={term}>
            <b>{term}</b>
            {text}
          </div>
        ))}
      </div>
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Troubleshooting
      </Typography>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Symptom</th>
            <th>Cause</th>
            <th>Check / fix</th>
          </tr>
        </thead>
        <tbody>
          {PROBLEMS.map(([s, c, f]) => (
            <tr key={s}>
              <td>{s}</td>
              <td>{c}</td>
              <td>
                <code>{f}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Leftovers without effect
      </Typography>
      {leftovers.map((l) => (
        <Hint key={l.text}>{l.text}</Hint>
      ))}
    </>
  );
};

export const RedirectGuides: React.FunctionComponent = () => {
  const { classes } = useStyles();
  const [guide, setGuide] = React.useState<GuideId>("find");
  const [domain, setDomain] = React.useState("");
  const [version, setVersion] = React.useState(0);
  const open = (next: GuideId, d: string) => {
    setDomain(d);
    setVersion((v) => v + 1);
    setGuide(next);
  };
  const current = GUIDES.find((g) => g.id === guide)!;
  const key = `${guide}-${version}`;

  return (
    <Box className={classes.root}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        IT guides
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Step by step, with all commands ready to copy. No nginx or Apache knowledge needed – tick each step when it is done.
      </Typography>
      <Tabs value={guide} onChange={(_, v) => setGuide(v)} variant="scrollable" scrollButtons="auto" className={classes.tabs}>
        {GUIDES.map((g) => (
          <Tab key={g.id} value={g.id} label={g.label} />
        ))}
      </Tabs>
      <Paper variant="outlined" className={classes.panel} sx={{ mt: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {current.text}
        </Typography>
        {guide === "find" && <FindGuide open={open} />}
        {guide === "change" && <ChangeGuide key={key} initial={domain} />}
        {guide === "new" && <NewGuide key={key} initial={domain} open={open} />}
        {guide === "plesk" && <PleskGuide key={key} initial={domain} open={open} />}
        {guide === "dns" && <DnsGuide key={key} initial={domain} />}
        {guide === "help" && <HelpGuide />}
      </Paper>
    </Box>
  );
};
